import * as React from 'react';

interface PlanChange {
  planName: string;
  oldQuantity: number;
  newQuantity: number;
  pricePerUnit: string;
  oldTotal: string;
  newTotal: string;
}

interface SubscriptionUpdateEmailProps {
  name?: string;
  company?: string;
  supportEmail?: string;
  domain?: string;
  subscription?: {
    updateType: 'increase' | 'decrease';
    updateDate: string;
    effectiveDate: string;
    planChanges: PlanChange[];
    newBillingTotal: string;
    nextBillingDate: string;
  };
  accountLink?: string;
  invoiceLink?: string;
}

export const SubscriptionUpdateEmail: React.FC<SubscriptionUpdateEmailProps> = ({
  name = 'João Silva',
  company = 'Trivium',
  supportEmail = 'suporte@trivium.com.br',
  domain = 'trivium.com.br',
  subscription = {
    updateType: 'increase',
    updateDate: '15/08/2023',
    effectiveDate: '15/08/2023',
    planChanges: [
      {
        planName: 'Licença Profissional',
        oldQuantity: 5,
        newQuantity: 10,
        pricePerUnit: 'R$ 99,90',
        oldTotal: 'R$ 499,50',
        newTotal: 'R$ 999,00'
      },
      {
        planName: 'Add-on: Analytics Avançado',
        oldQuantity: 2,
        newQuantity: 4,
        pricePerUnit: 'R$ 49,90',
        oldTotal: 'R$ 99,80',
        newTotal: 'R$ 199,60'
      }
    ],
    newBillingTotal: 'R$ 1.198,60',
    nextBillingDate: '15/09/2023'
  },
  accountLink = 'https://trivium.com.br/minha-conta',
  invoiceLink = 'https://trivium.com.br/minha-conta/faturas/atual',
}) => {
  const isIncrease = subscription.updateType === 'increase';
  
  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <img 
          src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Logo%20Trivium%20Rebranded%20Branco%20150%20dpi-0VfiiO2tOZNdZlvY5A7z3saYZGysgq.png" 
          alt="Trivium"
          style={logoImageStyle}
        />
      </div>
      
      {/* Content */}
      <div style={contentStyle}>
        {/* Banner de Atualização */}
        <table cellPadding="0" cellSpacing="0" border={0} width="100%" style={{ 
          backgroundColor: isIncrease ? '#E8F5E9' : '#FFF3E0',
          borderRadius: '8px 8px 0 0'
        }}>
          <tr>
            <td align="center" style={{ padding: '40px 20px' }}>
              <table cellPadding="0" cellSpacing="0" border={0} width="100%" style={{ maxWidth: '400px' }}>
                {/* Círculo com ícone */}
                <tr>
                  <td align="center" style={{ paddingBottom: '30px' }}>
                    <div style={{ 
                      width: '60px', 
                      height: '60px', 
                      borderRadius: '50%', 
                      backgroundColor: isIncrease ? '#4CAF50' : '#FF9800', 
                      display: 'inline-block',
                      textAlign: 'center',
                      lineHeight: '60px'
                    }}>
                      {isIncrease ? (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ verticalAlign: 'middle' }}>
                          <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6h-6z" fill="white"/>
                        </svg>
                      ) : (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ verticalAlign: 'middle' }}>
                          <path d="M16 18l2.29-2.29-4.88-4.88-4 4L2 7.41 3.41 6l6 6 4-4 6.3 6.29L22 12v6h-6z" fill="white"/>
                        </svg>
                      )}
                    </div>
                  </td>
                </tr>
                
                {/* Título */}
                <tr>
                  <td align="center" style={{ paddingBottom: '15px' }}>
                    <h1 style={{ 
                      margin: '0',
                      fontSize: '28px',
                      color: '#333333',
                      fontWeight: '600'
                    }}>
                      {isIncrease ? 'Assinatura Ampliada' : 'Assinatura Reduzida'}
                    </h1>
                  </td>
                </tr>
                
                {/* Mensagem */}
                <tr>
                  <td align="center" style={{ paddingBottom: '25px' }}>
                    <p style={{ 
                      margin: '0',
                      fontSize: '16px',
                      color: '#666666'
                    }}>
                      {isIncrease 
                        ? 'Sua assinatura foi atualizada com mais licenças' 
                        : 'Sua assinatura foi atualizada com menos licenças'}
                    </p>
                  </td>
                </tr>
                
                {/* Data de Efetivação */}
                <tr>
                  <td align="center">
                    <div style={{ 
                      backgroundColor: isIncrease ? '#4CAF50' : '#FF9800',
                      borderRadius: '24px',
                      color: '#FFFFFF',
                      fontWeight: '600',
                      fontSize: '15px',
                      padding: '12px 25px',
                      display: 'inline-block'
                    }}>
                      Efetivo a partir de: {subscription.effectiveDate}
                    </div>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>
        
        <div style={contentContainerStyle}>
          <h2 style={titleStyle}>Olá, {name}!</h2>
          
          <p style={paragraphStyle}>
            Este é um aviso para confirmar que a quantidade de licenças em sua assinatura foi {isIncrease ? 'aumentada' : 'reduzida'} conforme solicitado.
            As alterações foram processadas em {subscription.updateDate} e já estão em vigor.
          </p>
        </div>
        
        {/* Tabela com as alterações */}
        <div style={detailsCardStyle}>
          <h3 style={detailsHeadingStyle}>Detalhes da Atualização</h3>
          
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={tableHeaderStyle}>Plano</th>
                <th style={tableHeaderStyle}>Qtd. Anterior</th>
                <th style={tableHeaderStyle}>Nova Qtd.</th>
                <th style={tableHeaderStyle}>Preço Unit.</th>
                <th style={tableHeaderStyle}>Novo Total</th>
              </tr>
            </thead>
            <tbody>
              {subscription.planChanges.map((plan, index) => (
                <tr key={index} style={index % 2 === 0 ? tableRowEvenStyle : tableRowOddStyle}>
                  <td style={tableCellStyle}><strong>{plan.planName}</strong></td>
                  <td style={tableCellStyle}>{plan.oldQuantity}</td>
                  <td style={tableCellStyle}><strong>{plan.newQuantity}</strong></td>
                  <td style={tableCellStyle}>{plan.pricePerUnit}</td>
                  <td style={tableCellStyle}><strong>{plan.newTotal}</strong></td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={4} style={tableTotalLabelStyle}>Novo valor mensal total:</td>
                <td style={tableTotalValueStyle}>{subscription.newBillingTotal}</td>
              </tr>
            </tfoot>
          </table>
          
          <p style={tableCaptionStyle}>
            <strong>Próxima data de faturamento:</strong> {subscription.nextBillingDate}
          </p>
        </div>
        
        <div style={actionsContainerStyle}>
          <p style={ctaTextStyle}>
            Você pode gerenciar sua assinatura e visualizar os detalhes completos no seu painel:
          </p>
          <div style={buttonContainerStyle}>
            <a href={accountLink} style={buttonStyle}>
              Gerenciar Assinatura
            </a>
          </div>
          
          <p style={ctaTextStyle}>
            Ou visualizar sua fatura atualizada:
          </p>
          <div style={secondaryButtonContainerStyle}>
            <a href={invoiceLink} style={secondaryButtonStyle}>
              Ver Fatura
            </a>
          </div>
        </div>
        
        <div style={noteStyle}>
          <div style={noteIconStyle}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill={isIncrease ? '#4CAF50' : '#FF9800'}/>
            </svg>
          </div>
          <p style={noteTextStyle}>
            <strong>Observação:</strong> {isIncrease 
              ? 'O valor cobrado será proporcional ao período restante do ciclo de faturamento atual.' 
              : 'Se aplicável, um crédito proporcional será aplicado à sua próxima fatura.'}
          </p>
        </div>
        
        <hr style={hrStyle} />
        
        <div style={footerContentStyle}>
          <p style={footerStyle}>
            Se você tiver alguma dúvida sobre esta alteração ou sobre sua assinatura, entre em contato conosco:
          </p>
          <div style={contactChannelsStyle}>
            <p style={contactChannelStyle}>✉️ {supportEmail}&nbsp;&nbsp;📱 (11) 3181-5940</p>
          </div>
          <p style={footerStyle}>
            Atenciosamente,<br />
            Equipe {company}
          </p>
        </div>
      </div>
      
      {/* Footer */}
      <div style={emailFooterStyle}>
        <p style={copyrightStyle}>
          © 2025 {company}. Todos os direitos reservados.
        </p>
        <div style={footerLinksStyle}>
          <a href={`https://${domain}/termos`} style={footerLinkStyle}>Termos de Uso</a>
          <span style={footerDividerStyle}>|</span>
          <a href={`https://${domain}/privacidade`} style={footerLinkStyle}>Política de Privacidade</a>
        </div>
      </div>
    </div>
  );
};

// Estilos
const brandColor = '#1F1746';
const accentColor = '#4CAF50';
const backgroundColor = '#F5F7FA';
const textColor = '#333333';
const secondaryTextColor = '#666666';

const containerStyle = {
  fontFamily: "'Satoshi', Arial, sans-serif",
  maxWidth: '600px',
  margin: '0 auto',
  backgroundColor: backgroundColor,
};

const headerStyle = {
  backgroundColor: brandColor,
  padding: '25px',
  textAlign: 'center' as const,
  borderTopLeftRadius: '8px',
  borderTopRightRadius: '8px',
};

const logoImageStyle = {
  maxWidth: '180px',
  height: 'auto',
  display: 'block',
  margin: '0 auto',
};

const contentStyle = {
  backgroundColor: '#ffffff',
  borderRadius: '0 0 8px 8px',
  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.05)',
  padding: '0',
  margin: '0 0 20px 0',
  overflow: 'hidden',
};

const contentContainerStyle = {
  padding: '0 40px',
};

const titleStyle = {
  fontSize: '24px',
  fontWeight: '600',
  color: brandColor,
  marginBottom: '16px',
  padding: '24px 0 0 0',
};

const paragraphStyle = {
  fontSize: '16px',
  lineHeight: '24px',
  color: textColor,
  margin: '16px 0',
};

const detailsCardStyle = {
  backgroundColor: '#F8F9FA',
  borderRadius: '8px',
  margin: '24px 40px',
  padding: '24px',
  overflowX: 'auto' as const,
};

const detailsHeadingStyle = {
  fontSize: '18px',
  fontWeight: '600',
  color: brandColor,
  margin: '0 0 20px 0',
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse' as const,
  fontSize: '14px',
};

const tableHeaderStyle = {
  backgroundColor: brandColor,
  color: '#ffffff',
  padding: '12px',
  textAlign: 'left' as const,
  fontWeight: '600',
};

const tableRowEvenStyle = {
  backgroundColor: '#ffffff',
};

const tableRowOddStyle = {
  backgroundColor: '#f9f9f9',
};

const tableCellStyle = {
  padding: '12px',
  borderBottom: '1px solid #e1e4e8',
};

const tableTotalLabelStyle = {
  padding: '12px',
  textAlign: 'right' as const,
  fontWeight: '600',
  fontSize: '15px',
};

const tableTotalValueStyle = {
  padding: '12px',
  fontWeight: '700',
  fontSize: '16px',
  color: brandColor,
};

const tableCaptionStyle = {
  fontSize: '14px',
  color: secondaryTextColor,
  marginTop: '16px',
  textAlign: 'right' as const,
};

const actionsContainerStyle = {
  textAlign: 'center' as const,
  padding: '0 40px 24px 40px',
};

const buttonContainerStyle = {
  margin: '24px 0',
};

const buttonStyle = {
  backgroundColor: accentColor,
  color: '#ffffff',
  textDecoration: 'none',
  fontWeight: '600',
  fontSize: '16px',
  padding: '12px 24px',
  borderRadius: '6px',
  display: 'inline-block',
};

const secondaryButtonContainerStyle = {
  margin: '16px 0',
};

const secondaryButtonStyle = {
  backgroundColor: '#ffffff',
  color: brandColor,
  textDecoration: 'none',
  fontWeight: '600',
  fontSize: '16px',
  padding: '10px 20px',
  borderRadius: '6px',
  display: 'inline-block',
  border: `1px solid ${brandColor}`,
};

const ctaTextStyle = {
  fontSize: '15px',
  color: secondaryTextColor,
  margin: '8px 0',
};

const noteStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  backgroundColor: '#F8F9FA',
  borderRadius: '6px',
  padding: '16px 20px',
  margin: '0 40px 24px 40px',
};

const noteIconStyle = {
  marginRight: '12px',
  flexShrink: 0,
};

const noteTextStyle = {
  fontSize: '15px',
  color: textColor,
  margin: '0',
  lineHeight: '1.5',
};

const hrStyle = {
  borderColor: '#E1E4E8',
  margin: '24px 40px',
};

const footerContentStyle = {
  padding: '0 40px 32px 40px',
};

const footerStyle = {
  fontSize: '14px',
  color: secondaryTextColor,
  margin: '8px 0',
};

const contactChannelsStyle = {
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'center',
  marginBottom: '20px',
  marginTop: '8px',
};

const contactChannelStyle = {
  fontSize: '15px',
  color: textColor,
  margin: '6px 0',
  fontWeight: '500',
};

const emailFooterStyle = {
  backgroundColor: backgroundColor,
  padding: '24px 40px',
  textAlign: 'center' as const,
  borderBottomLeftRadius: '8px',
  borderBottomRightRadius: '8px',
};

const copyrightStyle = {
  fontSize: '13px',
  color: secondaryTextColor,
  margin: '0 0 12px 0',
};

const footerLinksStyle = {
  margin: '8px 0',
};

const footerLinkStyle = {
  color: brandColor,
  textDecoration: 'none',
  fontSize: '13px',
  fontWeight: '500',
};

const footerDividerStyle = {
  color: secondaryTextColor,
  margin: '0 8px',
};

export default SubscriptionUpdateEmail; 